"use client";

import { quoteThemeColorStyles, quoteThemeCssVars } from "../quote-theme/quote-theme-css-vars";
import { Mail, Phone, Calendar, Clock } from "lucide-react";
import { useHolidayQuoteBrand } from "../contexts/use-holiday-quote-brand";
import { usePrimaryColorStyles } from "../contexts/use-primary-color-styles";
import { iAgentDataBlock } from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";
import { HtmlContentWithReadMore } from "@/components/common/html-content-with-read-more";

export interface iAgentSectionData {
  name: string;
  role?: string;
  image?: string;
  email: string;
  phone?: string;
  availability?: {
    days?: string;
    hours?: string;
  };
  bio?: string;
  customMessage?: string;
}

interface AgentSectionProps extends iAgentDataBlock {
  agent?: iAgentSectionData;
  primaryColor?: string;
}

const defaultAgent = {
  name: "Sarah Thompson",
  role: "Senior Travel Specialist",
  image:
    "https://tprofileapp.blob.core.windows.net/3ba7587e-d08b-43ae-fb17-08dd106b6a53/449464337_784450343885429_5744650365700406825_n_(3).jpg",
  email: "sarah.t@travelcompany.com",
  phone: "+44 20 7123 4567",
  availability: {
    days: "Monday - Friday",
    hours: "9:00 AM - 5:30 PM GMT",
  },
  bio: "With over 10 years of experience in luxury travel, I specialize in creating unforgettable Mediterranean experiences.",
};

export function AgentSection({
  primaryColor = "#3B82F6",
  customMessage,
}: AgentSectionProps) {
  const { currentAgent: agent } = useHolidayQuoteBrand();
  const { containerStyle, textIconStyle } = usePrimaryColorStyles();

  if (!agent) {
    return <></>;
  }

  return (
    <div className="bg-white rounded-3xl shadow-sm p-4 sm:p-6">
      <h2 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6">Your Travel Specialist</h2>

      <div className="space-y-4 sm:space-y-6">
        {/* Agent Profile Header */}
        <div className="flex items-center gap-3 sm:gap-4">
          {agent.image ? (
            <div className="relative w-12 h-12 sm:w-16 sm:h-16 rounded-full overflow-hidden bg-gray-200">
              <img
                src={agent.image}
                alt={agent.name}
                className="object-cover w-full h-full"
              />
            </div>
          ) : null}
          <div>
            <h3 className="text-base sm:text-lg font-medium text-gray-900">{agent.name}</h3>
            {agent.role && (
              <p className="text-sm text-gray-600">{agent.role}</p>
            )}
          </div>
        </div>

        {/* Contact Information */}
        <div className="space-y-2 sm:space-y-3">
          <div className="flex items-center gap-2 sm:gap-3">
            <div
              className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center"
              style={containerStyle}
            >
              <Mail className="w-4 h-4 sm:w-5 sm:h-5" style={textIconStyle} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Email</p>
              <a
                href={`mailto:${agent.email}`}
                className="hover:underline text-sm"
                style={quoteThemeColorStyles.primary}
              >
                {agent.email}
              </a>
            </div>
          </div>

          {agent.phone && (
            <div className="flex items-center gap-2 sm:gap-3">
              <div
                className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center"
                style={containerStyle}
              >
                <Phone className="w-4 h-4 sm:w-5 sm:h-5" style={textIconStyle} />
              </div>
              <div>
                <p className="text-sm text-gray-600">Phone</p>
                <a
                  href={`tel:${agent.phone}`}
                  className="hover:underline text-sm"
                  style={quoteThemeColorStyles.primary}
                >
                  {agent.phone}
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Availability */}
        {agent.availability?.days && agent.availability?.hours && (
          <div className="bg-gray-50 rounded-xl p-3 sm:p-4 space-y-2 sm:space-y-3">
            <h4 className="font-medium text-sm sm:text-base text-gray-900">Available Hours</h4>
            <div className="space-y-1.5 sm:space-y-2">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar className="w-4 h-4" style={quoteThemeColorStyles.primary} />
                <span>{agent.availability.days}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Clock className="w-4 h-4" style={quoteThemeColorStyles.primary} />
                <span>{agent.availability.hours}</span>
              </div>
            </div>
          </div>
        )}

        {/* Bio */}
        {agent.bio && (
          <div className="space-y-1.5 sm:space-y-2">
            <h4 className="font-medium text-sm sm:text-base text-gray-900">About Me</h4>
            {/* Mobile: Use HtmlContentWithReadMore for compact display */}
            <div className="sm:hidden">
              <HtmlContentWithReadMore
                html={agent.bio}
                maxWords={30}
                className="text-sm text-gray-600 leading-relaxed"
              />
            </div>
            {/* Desktop: Regular display */}
            <div className="hidden sm:block">
              <p className="text-sm text-gray-600 leading-relaxed">{agent.bio}</p>
            </div>
          </div>
        )}

        {/* Custom Message */}
        {customMessage && (
          <div className="space-y-1.5 sm:space-y-2">
            <h4 className="font-medium text-sm sm:text-base text-gray-900">Personal Message</h4>
            {/* Mobile: Use HtmlContentWithReadMore for compact display */}
            <div className="sm:hidden">
              <HtmlContentWithReadMore
                html={customMessage}
                maxWords={30}
                className="text-sm text-gray-600 leading-relaxed"
              />
            </div>
            {/* Desktop: Regular HTML display */}
            <div className="hidden sm:block">
              <div
                className="text-sm text-gray-600 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: customMessage }}
              />
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          <button
            onClick={() => (window.location.href = `mailto:${agent.email}`)}
            className="flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 
                     rounded-xl font-medium text-sm
                     hover:brightness-90 focus:outline-none focus:ring-2 
                     focus:ring-offset-2 transition-colors"
            style={{
              backgroundColor: quoteThemeCssVars.primary,
              color: "white",
              borderColor: quoteThemeCssVars.primary,
            }}
          >
            <Mail className="w-4 h-4" />
            <span className="hidden sm:inline">Email Me</span>
            <span className="sm:hidden">Email</span>
          </button>

          {agent.phone && (
            <button
              onClick={() => (window.location.href = `tel:${agent.phone}`)}
              className="flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 
                       border rounded-xl font-medium text-sm
                       hover:bg-gray-50 focus:outline-none focus:ring-2 
                       focus:ring-offset-2 transition-colors"
              style={{
                color: quoteThemeCssVars.primary,
                borderColor: quoteThemeCssVars.primary,
              }}
            >
              <Phone className="w-4 h-4" />
              <span className="hidden sm:inline">Call Me</span>
              <span className="sm:hidden">Call</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
